import { useState, useEffect, useCallback } from 'react';
import { usePlanMetadataService } from '../../../servicecontexts/PlanMetadataServiceContext';
import type { PlanMetadata } from '../services/PlanMetadataService';

export interface UsePlanMetadataReturn {
    metadata: Map<string, PlanMetadata>;
    loading: boolean;
    publishPlan: (planId: string, kommunenummer?: number) => Promise<void>;
    unpublishPlan: (planId: string) => Promise<void>;
    registrerRedigering: (planId: string, kommunenummer?: number, seksjonCountDelta?: number) => void;
    updateBeskrivelse: (planId: string, beskrivelse: string, kommunenummer?: number) => Promise<void>;
    updatePlanregisterlink: (planId: string, url: string, kommunenummer?: number) => Promise<void>;
    updateGjeldendeBestemmelser: (planId: string, url: string, kommunenummer?: number) => Promise<void>;
}

export function usePlanMetadata(): UsePlanMetadataReturn {
    const service = usePlanMetadataService();
    const [metadata, setMetadata] = useState<Map<string, PlanMetadata>>(new Map());
    const [loading, setLoading] = useState(false);

    const load = useCallback(() => {
        setLoading(true);
        service.fetchMetadata()
            .then(setMetadata)
            .catch(console.error)
            .finally(() => setLoading(false));
    }, [service]);

    useEffect(() => { load(); }, [load]);

    const publishPlan = useCallback(async (planId: string, kommunenummer?: number) => {
        await service.publishPlan(planId, kommunenummer);
        setMetadata(prev => {
            const next = new Map(prev);
            const existing = next.get(planId);
            next.set(planId, {
                planId,
                kommunenummer: existing?.kommunenummer ?? kommunenummer,
                publisert: true,
                publisertDato: new Date(),
                sistRedigertDato: new Date(),
                seksjonCount: existing?.seksjonCount ?? 0,
                antallVisninger: existing?.antallVisninger ?? 0,
            });
            return next;
        });
    }, [service]);

    const unpublishPlan = useCallback(async (planId: string) => {
        await service.unpublishPlan(planId);
        setMetadata(prev => {
            const next = new Map(prev);
            const existing = next.get(planId);
            next.set(planId, {
                planId,
                kommunenummer: existing?.kommunenummer,
                publisert: false,
                publisertDato: null,
                sistRedigertDato: existing?.sistRedigertDato ?? null,
                seksjonCount: existing?.seksjonCount ?? 0,
                antallVisninger: existing?.antallVisninger ?? 0,
            });
            return next;
        });
    }, [service]);

    const registrerRedigering = useCallback((planId: string, kommunenummer?: number, seksjonCountDelta?: number) => {
        const now = new Date();
        setMetadata(prev => {
            const existing = prev.get(planId);
            const next = new Map(prev);
            if (existing) {
                next.set(planId, {
                    ...existing,
                    sistRedigertDato: now,
                    // Only increment (add). Never decrement: a wrong local count must
                    // not produce a false "ikke påbegynt". The server count corrects on reload.
                    seksjonCount: seksjonCountDelta && seksjonCountDelta > 0
                        ? existing.seksjonCount + seksjonCountDelta
                        : existing.seksjonCount,
                });
            } else if (seksjonCountDelta && seksjonCountDelta > 0) {
                // Plan not yet in the map — only create an entry on add (delta > 0)
                // to avoid setting seksjonCount: 0 for plans that already have sections.
                next.set(planId, {
                    planId,
                    kommunenummer,
                    publisert: false,
                    publisertDato: null,
                    sistRedigertDato: now,
                    seksjonCount: seksjonCountDelta,
                    antallVisninger: 0,
                });
            }
            return next;
        });
        service.registrerRedigering(planId, kommunenummer).catch(console.error);
    }, [service]);

    const updateBeskrivelse = useCallback(async (planId: string, beskrivelse: string, kommunenummer?: number) => {
        await service.updateBeskrivelse(planId, beskrivelse, kommunenummer);
        const now = new Date();
        setMetadata(prev => {
            const next = new Map(prev);
            const existing = next.get(planId);
            if (existing) {
                next.set(planId, { ...existing, PlanensBeskrivelse: beskrivelse, sistRedigertDato: now });
            }
            return next;
        });
        service.registrerRedigering(planId, kommunenummer).catch(console.error);
    }, [service]);

    const updatePlanregisterlink = useCallback(async (planId: string, url: string, kommunenummer?: number) => {
        await service.updatePlanregisterlink(planId, url, kommunenummer);
        const now = new Date();
        setMetadata(prev => {
            const next = new Map(prev);
            const existing = next.get(planId);
            if (existing) {
                next.set(planId, { ...existing, Planregisterlink: url, sistRedigertDato: now });
            }
            return next;
        });
        service.registrerRedigering(planId, kommunenummer).catch(console.error);
    }, [service]);

    const updateGjeldendeBestemmelser = useCallback(async (planId: string, url: string, kommunenummer?: number) => {
        await service.updateGjeldendeBestemmelser(planId, url, kommunenummer);
        const now = new Date();
        setMetadata(prev => {
            const next = new Map(prev);
            const existing = next.get(planId);
            if (existing) {
                next.set(planId, { ...existing, GjeldendeBestemmelser: url, sistRedigertDato: now });
            }
            return next;
        });
        service.registrerRedigering(planId, kommunenummer).catch(console.error);
    }, [service]);

    return { metadata, loading, publishPlan, unpublishPlan, registrerRedigering, updateBeskrivelse, updatePlanregisterlink, updateGjeldendeBestemmelser };
}
